// Authenticated HTTP stays at Gab's origin. The extension receives only a
// short-lived connection ID and task-scoped commands, never login credentials.
//
// Backend commands expire 25s after dispatch (extension_work timeoutMs) and
// /poll redelivers the pending command until a result lands. The client
// therefore keeps polling while a command is in flight — dispatching each
// command id to the extension once — and gives up loudly once the backend
// can no longer accept the result, instead of stalling silently forever.
const RESULT_TIMEOUT_MS = 30000;
const transient = error => error?.data?.code !== 'ABORTED' &&
  (error?.status === 0 || error?.status >= 500);

export function createBrowserWorkClient({ request, send, conversationId, leaseOwner, current = () => true, schedule = setTimeout, cancel = clearTimeout, now = Date.now }) {
  let active = true, connection, timer, dispatchedId = null, dispatchedAt = 0, completedId = null;
  let polling = false, submittingId = null, pollFailures = 0;
  const valid = () => active && current();
  // fetchWithAuth owns JSON serialization. Passing a JSON string here encodes
  // it a second time, so the backend never receives the conversation/lease.
  const post = (action, body = {}) => request(`/json/extension/work/${action}`, { method: 'POST', networkRetries: 0,
    body: { conversationId, ...body }, headers: { 'Content-Type': 'application/json' }, timeoutMs: 10000 });
  const stop = () => {
    active = false; cancel(timer);
    const id = connection; connection = undefined;
    if (id) void post('disconnect', { id }).catch(() => {});
  };
  const poll = async () => {
    if (!valid() || polling) return;
    polling = true;
    let delay = 1000;
    try {
      const result = await post('poll', { id: connection });
      if (!valid()) return;
      if (pollFailures) send('work-restored', {});
      pollFailures = 0;
      if (!submittingId && result.command && result.command.id !== dispatchedId && result.command.id !== completedId) {
        dispatchedId = result.command.id; dispatchedAt = now();
        send('work-command', { command: result.command });
      } else if (!submittingId && dispatchedId && now() - dispatchedAt > RESULT_TIMEOUT_MS) {
        // Submitted results use the bounded HTTP delivery timeout. This
        // deadline applies only while the browser has not returned a result.
        send('work-error', { message: 'The browser did not return a result. Inspect the browser before retrying, then connect again.' });
        stop(); return;
      }
    } catch (e) {
      if (valid()) {
        // Retrying this read renews the SAME lease. It never grants new access
        // or replays an action; auth, revocation and conflict errors stay final.
        if (transient(e) && ++pollFailures <= 2) { delay = pollFailures * 1000; send('work-reconnecting', {}); }
        else { send('work-error', { message: e.message || 'Work disconnected. Connect again to continue.' }); stop(); }
      }
    } finally {
      polling = false;
      if (valid()) { cancel(timer); timer = schedule(poll, delay); }
    }
  };
  return {
    async start() {
      try {
        const result = await post('connect', leaseOwner ? { owner: leaseOwner } : {}); connection = result.id;
        if (!valid()) { stop(); return; }
        send('work-connected', { id: connection }); void poll();
      } catch (e) { if (valid()) send('work-error', { message: e.message || 'Could not connect Work.' }); }
    },
    async result(data) {
      if (!valid() || !dispatchedId || submittingId || data.connectionId !== connection || data.id !== dispatchedId) return;
      const operationId = dispatchedId;
      submittingId = operationId;
      try {
        let result = data.result;
        try { await post('result', { id: connection, operationId, result }); }
        catch (e) {
          if (!valid()) throw e;
          if (e.status === 413) {
            // A rejected body never reached the receipt route. Finish that
            // command with a bounded error, preserving the existing lease.
            result = { ok: false, code: 'browser-result-too-large',
              error: 'The browser result was too large to send. The connection is still available; inspect before retrying an action.' };
          } else if (!transient(e)) throw e;
          // Only retry delivery of the already-computed, idempotent receipt.
          // Never ask the extension to repeat the browser action.
          await post('result', { id: connection, operationId, result });
        }
      }
      catch (e) { if (valid()) { send('work-error', { message: e.message || 'Browser result could not be delivered. Inspect before retrying.' }); stop(); } }
      finally { completedId = operationId; if (dispatchedId === operationId) dispatchedId = null; submittingId = null; }
    },
    stop,
  };
}
